import api, { methods } from "@api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import type { Product } from "@type/product";

interface StockResponse {
  status: string;
  stock_status: Record<number, number | null>; // Simple map: product_id -> effective stock
  timestamp: number;
}

/**
 * Hook to keep product stock synchronized using React Query
 * - Polls every 5 seconds when app is active
 * - Refreshes on window focus (web) and app focus (mobile)
 * - Updates the global products query data with latest stock values
 * - Only fetches stock for products that have voorraad management
 */
export function useStockRefresh(enabled = true, products: Product[], productsQueryKey: any) {
  const queryClient = useQueryClient();
  const productIds = products.filter((p) => p.voorraad !== null && p.voorraad !== undefined).map((p) => p.id);

  // Use React Query for stock fetching with automatic refetch features
  const { data: stockData, refetch } = useQuery({
    queryKey: ["stock-status", productIds],
    queryFn: async () => {
      if (productIds.length === 0) {
        return null;
      }

      const response = await api<StockResponse>({
        path: "/stock/status",
        method: methods.POST,
        body: { product_ids: productIds },
      });

      // Validate response
      if (!response || typeof response !== "object") {
        console.log("Invalid stock status response");
        return null;
      }

      return response;
    },
    enabled: enabled && productIds.length > 0,
    refetchInterval: 5000, // Poll every 5 seconds
    refetchIntervalInBackground: false, // Don't poll in background
    refetchOnWindowFocus: true, // Refetch when window gains focus
    refetchOnReconnect: true, // Refetch when reconnecting
    staleTime: 2000, // Consider data stale after 2 seconds
    gcTime: 2000,
  });

  // Handle app state changes for mobile (since refetchOnWindowFocus is for web)
  useEffect(() => {
    if (enabled) {
      refetch();
    }
  }, [enabled, refetch]);

  // Update global products query data with new stock values
  useEffect(() => {
    if (!stockData || !stockData.stock_status) return;

    const stockMap = stockData.stock_status;

    // Update the products query data with new stock values
    queryClient.setQueryData<Product[]>(productsQueryKey, (oldProducts) => {
      if (!oldProducts) return oldProducts;

      return oldProducts.map((product) => {
        // Check if we have a stock update for this product
        const newStock = stockMap[product.id];

        // Only update if the stock value has changed
        if (newStock !== undefined && newStock !== product.voorraad) {
          return {
            ...product,
            voorraad: newStock, // Update voorraad with effective stock value
          };
        }

        return product;
      });
    });
  }, [stockData, queryClient, productsQueryKey]);
}
